// Legacy Journeys URL spellings, and how they map onto the paths the app's route
// patterns are written against.
//
// The Journeys IA rename moved a set of pages to new URLs: campaigns → automations,
// analysis → reports, outbox → message_activity, activity_logs → profile_activity,
// newsletters → one_time_sends. Those renamed paths are the ones the app serves today.
// Route patterns across the app were never rewritten, so they still spell the pages the
// old way, and live pathnames are normalized back to that spelling before matching.
//
// `/profiles` is the other direction: the People rename shipped and was then withdrawn,
// so the pages live at `/people` again and the `/profiles` spelling survives only as a
// redirect for bookmarks made while it was live. It normalizes here too, so a stale
// `/profiles` URL still matches the route that owns it.
//
// Ordered longest renamed fragment first, so a nested rename replaces before the path it
// sits under (`/journeys/message_activity/sent` before `/journeys/message_activity`).
const JOURNEYS_PATHNAME_ALIASES: { renamed: string; current: string }[] = [
  { renamed: '/journeys/profile_activity/identified', current: '/journeys/activity_logs' },
  { renamed: '/journeys/broadcasts/one_time_sends', current: '/journeys/broadcasts/newsletters' },
  { renamed: '/journeys/message_activity/drafts', current: '/journeys/outbox/drafts' },
  { renamed: '/journeys/message_activity/sent', current: '/journeys/outbox/deliveries' },
  { renamed: '/journeys/message_activity', current: '/journeys/outbox' },
  { renamed: '/reports/deliverability', current: '/analysis/deliverability' },
  { renamed: '/reports/email-clients', current: '/analysis/email-clients' },
  { renamed: '/journeys/automations', current: '/journeys/campaigns' },
  { renamed: '/journeys/profiles', current: '/journeys/people' },
  { renamed: '/journeys/reports', current: '/journeys/analysis' },
  { renamed: '/profiles/create', current: '/people/create' },
  { renamed: '/profiles/merge', current: '/people/merge' },
  { renamed: '/profiles/new', current: '/people/new' },
  { renamed: '/profiles', current: '/people' },
];

/**
 * Canonicalize a pathname to the vocabulary the app's route patterns are written in.
 *
 * Journeys pages are served at their renamed paths (`/journeys/automations`,
 * `/reports/deliverability`), and older spellings stay live for bookmarks. Route patterns
 * still use the original vocabulary (`/journeys/campaigns`, `/analysis/deliverability`),
 * so pathnames are normalized here before matching.
 */
export function normalizeJourneysPathname(pathname: string): string {
  for (const { renamed, current } of JOURNEYS_PATHNAME_ALIASES) {
    const index = pathname.indexOf(renamed);

    if (index === -1) {
      continue;
    }

    // Only replace at a segment boundary (end of path or a following `/`), so
    // e.g. `/journeys/reports` never matches inside `/journeys/reports-archive`.
    const nextChar = pathname[index + renamed.length];
    if (nextChar === undefined || nextChar === '/') {
      return pathname.slice(0, index) + current + pathname.slice(index + renamed.length);
    }
  }

  return pathname;
}
